import 'react-image-lightbox/style.css';
import React, {useContext, useEffect, useState, useRef, useMemo} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import useSEO, { SITE_URL, SITE_NAME } from '../../components/general/useSEO';
import {View, StyleSheet, Text, Modal, Pressable} from "react-native";
import {GlobalStateContext} from '../../components/general/GlobalStateProvider';
import DataOps from "../../data/DataOps";
import NotificationOps from "../../data/NotificationOps";
import {useLoadScript} from "@react-google-maps/api";
import { GOOGLE_MAPS_CONFIG } from '../../constants/GoogleMapsConfig';
import CampPageLocation from "./components/locations/CampPageLocation";
import useWindowSize from '../../utility/WindowSize';
import CoachViewModal from "../../components/session/CoachViewModal";
import {v4 as uuidv4} from 'uuid';
import {toast} from "react-toastify";
import SessionUtils from '../../utility/SessionUtils';
import CampInfoSection from './components/sections/CampInfoSection';
import DescriptionSection from './components/sections/DescriptionSection';
import SessionsViewSection from './components/sections/SessionsViewSection';

const LoadingBlock = ({ height, width = '100%', style = {} }: { height: number; width?: number | string; style?: any }) => (
    <View style={[styles.loadingBlock, { height, width }, style]} />
);

const CampPageLoadingScreen = ({ isSessionView = false }: { isSessionView?: boolean }) => (
    <View style={styles.loadingPage}>
        <View style={styles.loadingHeaderCard}>
            <LoadingBlock height={16} width={96} style={{ marginBottom: 14 }} />
            <LoadingBlock height={36} width="58%" style={{ marginBottom: 10 }} />
            <LoadingBlock height={18} width="34%" />
        </View>

        <View style={styles.loadingToggleRow}>
            <LoadingBlock height={40} width={138} style={styles.loadingPill} />
            <LoadingBlock height={40} width={138} style={styles.loadingPill} />
        </View>

        {isSessionView ? (
            <View style={styles.loadingSectionCard}>
                <LoadingBlock height={20} width={180} style={{ marginBottom: 18 }} />
                <LoadingBlock height={84} style={{ marginBottom: 14 }} />
                <LoadingBlock height={84} style={{ marginBottom: 14 }} />
                <LoadingBlock height={84} />
            </View>
        ) : (
            <>
                <View style={styles.loadingHeroCard}>
                    <LoadingBlock height={240} style={{ borderRadius: 24 }} />
                </View>

                <View style={styles.loadingGrid}>
                    <View style={styles.loadingSectionCard}>
                        <LoadingBlock height={20} width={180} style={{ marginBottom: 18 }} />
                        <LoadingBlock height={16} width="100%" style={{ marginBottom: 10 }} />
                        <LoadingBlock height={16} width="92%" style={{ marginBottom: 10 }} />
                        <LoadingBlock height={16} width="78%" />
                    </View>

                    <View style={styles.loadingSectionCard}>
                        <LoadingBlock height={20} width={160} style={{ marginBottom: 18 }} />
                        <LoadingBlock height={320} style={{ borderRadius: 18 }} />
                    </View>
                </View>

                <View style={styles.loadingSectionCard}>
                    <LoadingBlock height={20} width={150} style={{ marginBottom: 18 }} />
                    <LoadingBlock height={16} width="100%" style={{ marginBottom: 10 }} />
                    <LoadingBlock height={16} width="95%" style={{ marginBottom: 10 }} />
                    <LoadingBlock height={16} width="84%" />
                </View>
            </>
        )}

        <Text style={styles.loadingLabel}>Loading camp details...</Text>
    </View>
);

const CampScreenMain = ({setCampHost, isEditing = false, isSessionView = false}) => {
    const {userCampId: userCampId, user} = useContext(GlobalStateContext);

    const {width, height} = useWindowSize();
    const WIDTH = width;

    const {campId} = useParams();
    const [searchParams] = useSearchParams();
    const sessionIdFromUrl = searchParams.get('sessionId');
    const shouldOpenModal = searchParams.get('openModal');
    const shouldOpenCamperView = shouldOpenModal === 'camperView';
    const urlViewType = searchParams.get('view') as 'comments' | 'photos' | null;
    const urlDate = searchParams.get('date');
    const [isHost, setIsHost] = useState(false);
    const [camp, setCamp] = useState(null);
    const [sessions, setSessions] = useState({} as any);
    const [photos, setPhotos] = useState([]);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [activeTab, setActiveTab] = useState('Camp Sessions');
    const [sessionsViewMode, setSessionsViewMode] = useState<'cards' | 'table'>('cards');
    const [showCoachViewModal, setShowCoachViewModal] = useState(false);
    const [coachViewSessionId, setCoachViewSessionId] = useState<string | null>(null);
    const [coachViewCampId, setCoachViewCampId] = useState<string | null>(null);
    const [showPastSessions, setShowPastSessions] = useState(false);
    const [isLoadingCamp, setIsLoadingCamp] = useState(true);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [showNoCoachSessionModal, setShowNoCoachSessionModal] = useState(false);
    
    // Track previously saved session IDs to detect new sessions
    const previousSessionIdsRef = useRef(new Set());
    // JSON snapshots to detect changes
    const previousSessionsSnapshotRef = useRef<{ [key: string]: string }>({});

    // Check if current user can edit (either in edit mode or is the host)
    const canEdit = isEditing;
    
    const handleViewToggle = () => {
        setActiveTab(activeTab === 'General Information' ? 'Camp Sessions' : 'General Information');
    };

    const {isLoaded, loadError} = useLoadScript(GOOGLE_MAPS_CONFIG);

    // Ensure only minimal, stable coach fields are saved to backend
    const sanitizeSessionForSave = (raw: any) => {
        const next: any = { ...(raw || {}) };
        if (next && typeof next === 'object' && next.coach) {
            const c = next.coach || {};
            const userId = c.userId || c.id || null;
            const name = c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || null;
            const email = c.email || null;
            const phone = c.phone || null;
            const coachSanitized: any = { userId, name, email, phone };
            // Only include assignedAt if present; do not default to now to avoid false diffs
            if (c.assignedAt) {
                coachSanitized.assignedAt = new Date(c.assignedAt);
            }
            next.coach = coachSanitized;
        }
        // Do not persist deprecated instructor field
        if (next && typeof next === 'object' && 'instructor' in next) {
            delete next.instructor;
        }
        return next;
    };

    useEffect(() => {
        if (!camp) return;

        let invalidSessionsFound = false;

        const validateAndFilterSessions = () => {
            // During editing, allow sessions with empty fields (new sessions being created)
            if (isEditing) {
                return; // Skip validation during editing
            }

            // Validate but do not mutate sessions; if invalid, prevent save and keep editing
            for (const [, session] of Object.entries(sessions)) {
                const hasEmptyFields = Object.values(session as any).some(value => value === "");
                if (hasEmptyFields) {
                    invalidSessionsFound = true;
                    break;
                }
            }
            if (invalidSessionsFound) {
                toast.error('Please complete all required session fields before saving. You are still in edit mode.');
                try {
                    const ev = new CustomEvent('campEditValidationFailed');
                    window.dispatchEvent(ev);
                } catch {}
            }
        };

        validateAndFilterSessions();

        if (invalidSessionsFound) return;

        const saveSessions = async () => {
            if (!isEditing) {
                const newlyPublishedSessions = [];
                const updatedSessionTitles: string[] = [];
                
                for (const key of Object.keys(sessions)) {
                    try {
                        // Check if this is a newly published session
                        const isNewSession = !previousSessionIdsRef.current.has(key);
                        // Determine if session changed
                        let shouldSave = isNewSession;
                        if (!shouldSave) {
                            try {
                                const prevSnapshot = previousSessionsSnapshotRef.current[key] || '';
                                const currentSnapshot = JSON.stringify(sanitizeSessionForSave(sessions[key]) || {});
                                shouldSave = prevSnapshot !== currentSnapshot;
                            } catch {
                                shouldSave = true;
                            }
                        }
                        if (!shouldSave) {
                            continue;
                        }
                        
                        const sanitized = sanitizeSessionForSave(sessions[key]);
                        await DataOps.saveCustomDataToFirebase('session', key, sanitized, true);
                        
                        try { previousSessionsSnapshotRef.current[key] = JSON.stringify(sanitized || {}); } catch {}
                        updatedSessionTitles.push(sessions[key].title || 'session');
                        
                        if (isNewSession) {
                            previousSessionIdsRef.current.add(key);
                            newlyPublishedSessions.push({
                                sessionId: key,
                                session: sanitized
                            });
                        }
                    } catch (error) {
                        console.error(`Failed to save session with key ${key}:`, error);
                    }
                }

                // Aggregate toast messages for updated sessions
                if (updatedSessionTitles.length === 1) {
                    toast?.success?.(`Saved updates for ${updatedSessionTitles[0]}.`);
                } else if (updatedSessionTitles.length > 1) {
                    const list = updatedSessionTitles.join(', ');
                    toast?.success?.(`Updated ${updatedSessionTitles.length} sessions: [${list}]`);
                }

                // Send notifications for newly published sessions
                for (const {sessionId, session} of newlyPublishedSessions) {
                    try {
                        await NotificationOps.notifyNewSessionToFavorites(
                            camp.campId,
                            camp.campName || 'Camp',
                            session.title || 'New Session',
                            sessionId,
                            session.startDate || session.officialStartDate || new Date().toISOString()
                        );
                        
                        toast.success(`Notification sent to ${camp.campName} followers!`);
                    } catch (error) {
                        console.error(`Failed to send notifications for session ${sessionId}:`, error);
                    }
                }
            }
        };

        saveSessions();
    }, [isEditing]);

    // Centralized validation gate triggered by the Edit button when saving
    useEffect(() => {
        const handler = () => {
            // Validate current sessions; if invalid, show first missing field and keep edit mode
            for (const [, session] of Object.entries(sessions)) {
                const checks: { key: string; label: string; isInvalid: (v: any) => boolean }[] = [
                    { key: 'title', label: 'Session name', isInvalid: (v) => !v || (typeof v === 'string' && v.trim() === '') },
                    { key: 'startDate', label: 'Start date', isInvalid: (v) => !v },
                    { key: 'endDate', label: 'End date', isInvalid: (v) => !v },
                    { key: 'startTime', label: 'Start time', isInvalid: (v) => !v || (typeof v === 'string' && v.trim() === '') },
                    { key: 'endTime', label: 'End time', isInvalid: (v) => !v || (typeof v === 'string' && v.trim() === '') },
                    { key: 'sessionType', label: 'Session type', isInvalid: (v) => !v || v === '' },
                    { key: 'capacity', label: 'Class size', isInvalid: (v) => v === undefined || v === null || v === '' },
                    { key: 'price', label: 'Price', isInvalid: (v) => v === undefined || v === null || v === '' },
                ];

                for (const check of checks) {
                    const value = (session as any)[check.key];
                    if (check.isInvalid(value)) {
                        const name = ((session as any).title || 'Untitled session');
                        toast.error(`Please set ${check.label} for "${name}". You are still in edit mode.`);
                        try { window.dispatchEvent(new CustomEvent('campEditValidationFailed')); } catch {}
                        return;
                    }
                }
            }
            // If valid, exit edit mode
            try {
                const ev = new CustomEvent('campEditValidated');
                window.dispatchEvent(ev);
            } catch {}
        };
        window.addEventListener('requestCampValidation', handler);
        return () => window.removeEventListener('requestCampValidation', handler);
    }, [sessions]);

    // Initialize previousSessionIdsRef with existing sessions when camp loads
    useEffect(() => {
        const loadSessions = async () => {
            if (!camp?.campId) return;

            // Only needs to fetch upcoming sessions for regular users, but hosts can see all sessions.
            let campSessions = {};
            if (isHost) {
                const allCampSessions = await DataOps.fetchSessionsForCamp(camp.campId);
                campSessions = allCampSessions;
                
            } else {
                const UpcomingCampSessions = await DataOps.fetchUpcomingSessionsForCamp(camp.campId);
                campSessions = UpcomingCampSessions;
            }
            setSessions(campSessions);

            previousSessionIdsRef.current = new Set(Object.keys(campSessions));

            const snapshots: { [key: string]: string } = {};
            Object.entries(campSessions).forEach(([sid, sess]) => {
                try {
                    const sanitized = sanitizeSessionForSave(sess);
                    snapshots[sid] = JSON.stringify(sanitized || {});
                } catch {
                    snapshots[sid] = '';
                }
            });
            previousSessionsSnapshotRef.current = snapshots;
        };
        loadSessions();
    }, [camp?.campId]);

    useEffect(() => {
        const campIdFromRoute = campId || camp?.campId;
        const userId = user?.userId;
        const userAuthId = (user as any)?.authId;

        const userCamps = Array.isArray((user as any)?.camps) ? (user as any).camps : [];
        const userCampIds = Array.isArray((user as any)?.campIds) ? (user as any).campIds : [];

        // Robust ownership detection for hosts across legacy/new schemas.
        const isHostOwner =
            !!campIdFromRoute &&
            (
                userCampId === campIdFromRoute ||
                userCamps.includes(campIdFromRoute) ||
                userCampIds.includes(campIdFromRoute) ||
                (camp?.hostId && (camp.hostId === userId || camp.hostId === userAuthId))
            );

        // Coaches can still access coach/session views, but are not camp owners.
        const isCoachOfCamp = !!(user && camp?.campCoaches && camp.campCoaches.includes(user.userId));
        const hasCampAccess = isHostOwner || isCoachOfCamp;

        setIsHost(hasCampAccess);
        // Edit-camp button visibility should follow true host ownership only.
        setCampHost(isHostOwner);
    }, [userCampId, user, camp?.campId, camp?.hostId, camp?.campCoaches, campId]);

    // Handle sessionId from URL parameter (for notification navigation)
    useEffect(() => {
        if (sessionIdFromUrl && sessions && sessions[sessionIdFromUrl]) {
            setActiveTab('Camp Sessions');
            
            // Find the target session
            const targetSession = sessions[sessionIdFromUrl];
            if (targetSession) {
                // Scroll to sessions section if needed
                setTimeout(() => {
                    const sessionsElement = document.querySelector('[data-session-id="' + sessionIdFromUrl + '"]');
                    if (sessionsElement) {
                        sessionsElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);
            }
        }
    }, [sessionIdFromUrl, sessions]);

    // Open Coach View modal directly from URL params regardless of view mode
    useEffect(() => {
        if (shouldOpenCamperView && sessionIdFromUrl && camp?.campId) {
            setActiveTab('Camp Sessions');
            setCoachViewSessionId(sessionIdFromUrl);
            setCoachViewCampId(camp.campId);
            setShowCoachViewModal(true);
        }
    }, [shouldOpenCamperView, sessionIdFromUrl, camp?.campId]);

    const setPhotosWithCampData = (campData) => {
        if (!campData || !campData.campImageUrls) {
            return;
        }

        const campPhotos = []
        for (const imageUrl of campData.campImageUrls) {
            campPhotos.push({
                src: imageUrl,
                width: 'auto',
                height: 'auto',
            })
        }

        setPhotos(campPhotos);
    }

    const fetchCampSessions = async (campData) => {
        try {
            if (!campData?.campId) return;
            setIsLoadingSessions(true);
            const campSessions = await DataOps.fetchSessionsForCamp(campData.campId);
            setSessions(campSessions);
        } catch (error) {
            console.error('Failed to fetch camp sessions:', error);
        } finally {
            setIsLoadingSessions(false);
        }
    }

    useEffect(() => {
        const fetchCamp = async () => {
            try {
                setIsLoadingCamp(true);
                const campData = await DataOps.fetchDataFromFirebase('camp', campId);
                setCamp(campData);
                setPhotosWithCampData(campData);
                fetchCampSessions(campData);
            } catch (error) {
                console.error('Failed to fetch camp details:', error);
            } finally {
                setIsLoadingCamp(false);
            }
        };

        const unused_response = fetchCamp();
    }, [campId]);

    const renderContent = () => {
        if (!isSessionView) {
            return (
                <CampInfoSection
                    camp={camp}
                    setCamp={setCamp}
                    canEdit={canEdit}
                    currentUser={user}
                    setSelectedMarker={setSelectedMarker}
                    isMapLoaded={isLoaded}
                    mapLoadError={loadError}
                >
                    <DescriptionSection
                        camp={camp}
                        setCamp={setCamp}
                        canEdit={canEdit}
                    />
                </CampInfoSection>
            );
        }

        if (isLoadingSessions) {
            return <CampPageLoadingScreen isSessionView />;
        }
        
        return (
            <SessionsViewSection
                sessions={filteredSessionsMemo}
                sessionsViewMode={sessionsViewMode}
                setSessionsViewMode={setSessionsViewMode}
                showPastSessions={showPastSessions}
                setShowPastSessions={setShowPastSessions}
                canEdit={canEdit}
                isHost={isHost}
                camp={camp}
                addSession={addSession}
                setSessions={setSessions}
                urlViewType={urlViewType}
                urlDate={urlDate}
                sessionIdFromUrl={sessionIdFromUrl}
                shouldOpenCamperView={shouldOpenCamperView}
            />
        );
    };

    const createSessionDraft = () => {
        const sessionId = uuidv4();
        const newSession = SessionUtils.createDefaultSession(camp, sessionId);

        setSessions(prev => ({
            ...prev,
            [sessionId]: newSession,
        }));
    };

    const addSession = () => {
        const coachIds = Array.isArray(camp?.campCoaches) ? camp.campCoaches.filter(Boolean) : [];
        if (coachIds.length === 0) {
            setShowNoCoachSessionModal(true);
            return;
        }

        createSessionDraft();
    };

    const filteredSessionsMemo = useMemo(
        () => SessionUtils.filterAndSortSessions(sessions, showPastSessions, isEditing /* preserve order while editing */),
        [sessions, showPastSessions, isEditing]
    );

    // Build SEO metadata from camp data for search engines and AI crawlers
    const campSeoTitle = camp
        ? `${camp.campName} | Kids Summer Camp${camp.campCity ? ` in ${camp.campCity}` : ''} | ${SITE_NAME}`
        : undefined;
    const campSeoDescription = camp
        ? (camp.campDescription
            ? `${camp.campDescription.slice(0, 155).trim()}…`
            : `Book ${camp.campName}${camp.campCity ? ` in ${camp.campCity}` : ''} on SnapCamp. Find sessions, pricing, and registration details.`)
        : undefined;
    const campCanonical = campId ? `${SITE_URL}/camps/${campId}` : undefined;
    const campOgImage = camp?.campImageUrls?.[0] ?? undefined;

    const campJsonLd = camp ? [
        {
            '@context': 'https://schema.org',
            '@type': 'SportsActivityLocation',
            '@id': campCanonical,
            name: camp.campName,
            description: camp.campDescription || campSeoDescription,
            url: campCanonical,
            image: camp.campImageUrls?.[0] ?? undefined,
            telephone: camp.campPhone ?? undefined,
            email: camp.campEmail ?? undefined,
            address: camp.campCity || camp.campState ? {
                '@type': 'PostalAddress',
                addressLocality: camp.campCity ?? undefined,
                addressRegion: camp.campState ?? undefined,
                addressCountry: 'US',
            } : undefined,
            geo: camp.campLat && camp.campLng ? {
                '@type': 'GeoCoordinates',
                latitude: camp.campLat,
                longitude: camp.campLng,
            } : undefined,
            aggregateRating: camp.rating ? {
                '@type': 'AggregateRating',
                ratingValue: camp.rating,
                bestRating: 5,
                worstRating: 1,
                ratingCount: camp.ratingCount ?? 1,
            } : undefined,
        },
        // Individual sessions as CourseInstance / Event
        ...Object.values(filteredSessionsMemo).map((session: any) => ({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: session.title,
            description: session.description ?? undefined,
            startDate: session.startDate ?? session.officialStartDate ?? undefined,
            endDate: session.endDate ?? session.officialEndDate ?? undefined,
            location: {
                '@type': 'Place',
                name: camp.campName,
                address: camp.campCity || camp.campState ? {
                    '@type': 'PostalAddress',
                    addressLocality: camp.campCity ?? undefined,
                    addressRegion: camp.campState ?? undefined,
                    addressCountry: 'US',
                } : undefined,
            },
            organizer: {
                '@type': 'Organization',
                name: camp.campName,
                url: campCanonical,
            },
            offers: session.price !== undefined ? {
                '@type': 'Offer',
                price: session.price,
                priceCurrency: 'USD',
                availability: (session.capacity > 0)
                    ? 'https://schema.org/InStock'
                    : 'https://schema.org/SoldOut',
                url: `${campCanonical}?sessionId=${session.sessionId ?? ''}`,
            } : undefined,
            image: camp.campImageUrls?.[0] ?? undefined,
        })),
    ] : undefined;

    useSEO({
        title: campSeoTitle,
        description: campSeoDescription,
        canonical: campCanonical,
        ogImage: campOgImage,
        ogType: 'website',
        ogUrl: campCanonical,
        jsonLd: campJsonLd,
        noIndex: false,
    });

    const paddingHorizontal = WIDTH >= 1200 ? (WIDTH - 1200) / 2 : 100;

    return (
        (isLoadingCamp || !camp) ?
        <View
            style={{
                paddingHorizontal: paddingHorizontal,
                position: 'relative',
            }}
        >
            <CampPageLoadingScreen isSessionView={isSessionView} />
        </View>
        :
        <View
            style={
                {
                    paddingHorizontal: paddingHorizontal,
                    position: 'relative',
                }
            }
        >
            <View style={styles.stickyHeader}>
                <CampPageLocation 
                    camp={camp} 
                    setCamp={setCamp} 
                    isHost={isHost} 
                    isEditing={canEdit}
                    activeTab={activeTab}
                    onToggleView={handleViewToggle}
                />
            </View>

            {renderContent()}

            <CoachViewModal
                visible={showCoachViewModal}
                onClose={() => {
                    setShowCoachViewModal(false);
                    setCoachViewSessionId(null);
                    setCoachViewCampId(null);
                }}
                sessionId={coachViewSessionId}
                campId={coachViewCampId}
                currentUser={user}
                isCoach={isHost}
            />

            <Modal
                visible={showNoCoachSessionModal}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setShowNoCoachSessionModal(false)}
            >
                <View style={styles.noCoachModalOverlay}>
                    <View style={styles.noCoachModalCard}>
                        <Text style={styles.noCoachModalTitle}>No coaches assigned</Text>
                        <Text style={styles.noCoachModalText}>
                            Add coaches first, or continue with a TBD Coach.
                        </Text>
                        <View style={styles.noCoachModalActions}>
                            <Pressable
                                style={[styles.noCoachModalButton, styles.noCoachModalCancel]}
                                onPress={() => setShowNoCoachSessionModal(false)}
                            >
                                <Text style={styles.noCoachModalCancelText}>Cancel</Text>
                            </Pressable>
                            <Pressable
                                style={[styles.noCoachModalButton, styles.noCoachModalConfirm]}
                                onPress={() => {
                                    setShowNoCoachSessionModal(false);
                                    createSessionDraft();
                                }}
                            >
                                <Text style={styles.noCoachModalConfirmText}>Continue with TBD</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            </Modal>
        </View>
    );
};

const styles = StyleSheet.create({
    stickyHeader: {
        position: 'sticky' as any,
        top: 0,
        zIndex: 15,
        backgroundColor: 'white',
        width: '100%',
        paddingBottom: 20,
    },
    loadingPage: {
        paddingTop: 28,
        paddingBottom: 48,
    },
    loadingHeaderCard: {
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderRadius: 24,
        padding: 24,
        marginBottom: 24,
    },
    loadingToggleRow: {
        flexDirection: 'row',
        gap: 12,
        marginBottom: 24,
    },
    loadingPill: {
        borderRadius: 999,
    },
    loadingHeroCard: {
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderRadius: 24,
        padding: 20,
        marginBottom: 24,
    },
    loadingGrid: {
        flexDirection: 'column',
        gap: 24,
        marginBottom: 24,
    },
    loadingSectionCard: {
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderRadius: 24,
        padding: 24,
    },
    loadingBlock: {
        backgroundColor: '#E5E7EB',
        borderRadius: 12,
    },
    loadingLabel: {
        marginTop: 18,
        fontSize: 14,
        lineHeight: 22,
        color: '#6B7280',
        textAlign: 'center',
    },
    noCoachModalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 20,
    },
    noCoachModalCard: {
        width: '100%',
        maxWidth: 430,
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        padding: 20,
        borderWidth: 1,
        borderColor: '#E5E7EB',
    },
    noCoachModalTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#111827',
        marginBottom: 8,
    },
    noCoachModalText: {
        fontSize: 14,
        lineHeight: 20,
        color: '#4B5563',
    },
    noCoachModalActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: 18,
        gap: 10,
    },
    noCoachModalButton: {
        paddingHorizontal: 14,
        paddingVertical: 9,
        borderRadius: 9,
    },
    noCoachModalCancel: {
        borderWidth: 1,
        borderColor: '#D1D5DB',
        backgroundColor: '#FFFFFF',
    },
    noCoachModalConfirm: {
        borderWidth: 1,
        borderColor: '#2563EB',
        backgroundColor: '#2563EB',
    },
    noCoachModalCancelText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#374151',
    },
    noCoachModalConfirmText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#FFFFFF',
    },
});

export default CampScreenMain;
